/**
 * §A.5 · El equipo de Kiap: quién entra al back office · 12-10-2026
 *
 * ## No hay grados de acceso
 *
 * `users.is_platform_staff` es un booleano: quien está en esta lista ve los datos de
 * todos los clubes. Por eso la pantalla lo dice antes de nada, y por eso existe:
 * hasta hoy el acceso se daba con `KIAP_PERSONAL` en Railway y no había forma de
 * quitarlo sin tocar la base.
 *
 * ## Quitar corta al momento, y se confirma
 *
 * La API vuelve a mirar la base en cada petición del back office, así que quien lo
 * pierde está fuera en su siguiente clic. Se confirma en la propia fila, como
 * reprocesar un webhook, y dice qué le pasa a esa persona: la cuenta no se borra, y
 * si es dueño o familia en algún club sigue entrando a lo suyo.
 *
 * A uno mismo no se le ofrece: la API lo rechaza (`409 cannot_revoke_yourself`), y
 * eso es lo que impide que el back office se quede sin nadie.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import { aProblema, ErrorDeApi, type Problema } from '@/shared/api/errores';
import { Cargando, Error as PanelError } from '@/shared/ui/Estados';
import { fecha, fechaYHora } from '@/shared/ui/formato';

interface Miembro {
  readonly user_id: string;
  readonly email: string;
  readonly name: string;
  readonly last_login_at: Date | string | null;
  readonly created_at: Date | string;
  readonly is_you: boolean;
}

const CLAVE = ['admin', 'equipo'];

export function EquipoDeKiap() {
  const cache = useQueryClient();
  const [confirmando, setConfirmando] = useState<string | null>(null);
  const [correo, setCorreo] = useState('');
  const [nombre, setNombre] = useState('');
  const [hecho, setHecho] = useState<string | null>(null);
  const [problema, setProblema] = useState<Problema | null>(null);

  const equipo = useQuery({
    queryKey: CLAVE,
    queryFn: async () => {
      const { data, error } = await api.v1.admin.staff.get();
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as Miembro[];
    },
  });

  const dar = useMutation({
    mutationFn: async () => {
      const r = await api.v1.admin.staff.post({
        email: correo.trim(),
        first_name: nombre.trim() || null,
      });
      if (r.error) throw new ErrorDeApi(aProblema(r.error));
      return r.data as unknown as Miembro;
    },
    onSuccess: (nuevo) => {
      setProblema(null);
      setHecho(
        `${nuevo.email} ya tiene acceso. Que entre en app.kiapclubs.com con el enlace por correo; si tenía la sesión abierta, que salga y vuelva a entrar.`
      );
      setCorreo('');
      setNombre('');
      void cache.invalidateQueries({ queryKey: CLAVE });
    },
    onError: (e) => {
      setHecho(null);
      setProblema(aProblema(e));
    },
  });

  const quitar = useMutation({
    mutationFn: async (miembro: Miembro) => {
      const r = await api.v1.admin.staff({ userId: miembro.user_id }).delete();
      if (r.error) throw new ErrorDeApi(aProblema(r.error));
      return miembro;
    },
    onSuccess: (miembro) => {
      setConfirmando(null);
      setProblema(null);
      setHecho(`${miembro.email} ya no entra al back office.`);
      void cache.invalidateQueries({ queryKey: CLAVE });
    },
    onError: (e) => {
      setConfirmando(null);
      setHecho(null);
      setProblema(aProblema(e));
    },
  });

  return (
    <section>
      <h1>El equipo de Kiap</h1>

      <p className="tenue">
        Quien está aquí entra al back office y{' '}
        <strong>ve los datos de todos los clubes</strong>: no hay grados de
        acceso. Quitárselo a alguien corta al momento; su cuenta no se borra, y
        si es dueño o familia en algún club sigue entrando a lo suyo.
      </p>

      {hecho && (
        <div className="aviso aviso-calma" role="status">
          {hecho}
        </div>
      )}
      {problema && <PanelError problema={problema} />}

      {equipo.isPending && <Cargando filas={3} etiqueta="Cargando el equipo" />}

      {equipo.isError && (
        <PanelError
          problema={aProblema(equipo.error)}
          reintentar={() => void equipo.refetch()}
        />
      )}

      {equipo.data && (
        <div className="tabla-marco">
          <table className="tabla">
            <thead>
              <tr>
                <th>Quién</th>
                <th>Última entrada</th>
                <th>En el equipo desde</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {equipo.data.map((m) => (
                <tr key={m.user_id}>
                  <td>
                    {m.name}
                    <br />
                    <span className="tenue">{m.email}</span>
                  </td>
                  <td>
                    {m.last_login_at ? (
                      fechaYHora(m.last_login_at)
                    ) : (
                      <span className="tenue">Aún no ha entrado</span>
                    )}
                  </td>
                  <td>{fecha(m.created_at)}</td>
                  <td>
                    {m.is_you ? (
                      <span className="pastilla">Tú</span>
                    ) : confirmando === m.user_id ? (
                      <div className="hueco-acciones">
                        <button
                          type="button"
                          className="btn-peligro"
                          disabled={quitar.isPending}
                          onClick={() => quitar.mutate(m)}
                        >
                          {quitar.isPending
                            ? 'Quitando…'
                            : 'Sí, quitarle el acceso'}
                        </button>
                        <button
                          type="button"
                          className="btn-plano"
                          disabled={quitar.isPending}
                          onClick={() => setConfirmando(null)}
                        >
                          No
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        className="btn-plano"
                        onClick={() => {
                          setHecho(null);
                          setProblema(null);
                          setConfirmando(m.user_id);
                        }}
                      >
                        Quitar el acceso
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <form
        className="formulario"
        onSubmit={(e) => {
          e.preventDefault();
          setHecho(null);
          setProblema(null);
          dar.mutate();
        }}
      >
        <h2 className="seccion">Dar acceso a alguien</h2>
        <p className="tenue">
          Si el correo no tiene cuenta, se le crea sin contraseña: entra con el
          enlace por correo.{' '}
          <strong>No uses el correo de un dueño de club</strong>: quien tiene
          las dos cosas entra siempre al back office y no vería su club.
        </p>
        <div className="rejilla">
          <label>
            Correo
            <input
              type="email"
              required
              maxLength={200}
              autoComplete="off"
              value={correo}
              onChange={(e) => setCorreo(e.target.value)}
            />
          </label>
          <label>
            Nombre (opcional)
            <input
              maxLength={80}
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
            />
          </label>
        </div>
        <div className="acciones">
          <button
            type="submit"
            disabled={dar.isPending || correo.trim() === ''}
          >
            {dar.isPending ? 'Dando acceso…' : 'Dar acceso al back office'}
          </button>
        </div>
      </form>
    </section>
  );
}
